import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useLayoutEffect,
  useMemo,
} from 'react'
import type { Tick } from '@/lib/ruler/compute-ruler-ticks'
import { RULER_MAX_DOM_TICKS_PER_AXIS, RULER_OVERSCAN_PX } from '@/lib/ruler/ruler-tick-budgets'
import { viewportFilterAndCapTicks } from '@/lib/ruler/ruler-tick-viewport'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import { H_RULER_THICKNESS_PX, V_RULER_THICKNESS_PX } from '../constants/ruler-size'
import { computeRulerTickLayout } from '../helpers/ruler-ticks'

type RulerViewport = { sl: number; st: number; vw: number; vh: number }

/**
 * Ruler ticks for the whole scrollable range, then the subset worth putting in the DOM for the
 * current scroll/viewport. Re-measures the viewport and re-applies the strip transforms whenever
 * the tick geometry changes.
 */
export function useRulerTicks({
  scrollRef,
  documentCanvasRef,
  pasteboardRef,
  scale,
  pasteboardSize,
  rulerUnit,
  rulerContentOrigin,
  pasteboardScrollExtents,
  snappedSpreadOriginCanvasX,
  verticalRulerZeroAtCanvasY,
  rulerViewport,
  setRulerViewport,
  syncRulerScrollTransforms,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  documentCanvasRef: MutableRefObject<HTMLDivElement | null>
  pasteboardRef: MutableRefObject<HTMLDivElement | null>
  scale: number
  /** On-screen pasteboard size (`pasteboardSize` in `use-spread-layout`). */
  pasteboardSize: number
  rulerUnit: CenterCanvasConfig['rulerUnit']
  rulerContentOrigin: { x: number; y: number } | null
  pasteboardScrollExtents: { width: number; height: number }
  snappedSpreadOriginCanvasX: number
  verticalRulerZeroAtCanvasY: number
  rulerViewport: RulerViewport
  setRulerViewport: Dispatch<SetStateAction<RulerViewport>>
  syncRulerScrollTransforms: () => void
}) {
  // Ruler ticks: full spread in canvas space only — no scroll in deps. Pan is applied with CSS
  // translate on the tick layer so scrolling does not recompute tick geometry or labels.
  const rulerTicks = useMemo(() => {
    const scroller = scrollRef.current
    const canvasEl = documentCanvasRef.current

    const empty = {
      h: [] as Tick[],
      v: [] as Tick[],
      horizontalStripMinPx: pasteboardSize,
      verticalStripMinPx: pasteboardSize,
    }

    if (!scroller) return empty
    if (rulerUnit == null) return empty

    let originX = 0
    let originY = 0

    if (rulerContentOrigin) {
      originX = rulerContentOrigin.x
      originY = rulerContentOrigin.y
    } else if (canvasEl) {
      const sl = scroller.scrollLeft
      const st = scroller.scrollTop
      const scRect = scroller.getBoundingClientRect()
      const canvasRect = canvasEl.getBoundingClientRect()

      originX = sl + (canvasRect.left - scRect.left)
      originY = st + (canvasRect.top - scRect.top)
    }

    const viewWidth = scroller.clientWidth
    const viewHeight = scroller.clientHeight

    // Scrollable content size: prefer the scroller (authoritative for overflow:auto) and the
    // pasteboard, since transform overflow sometimes updates one before the other.
    const pbEl = pasteboardRef.current
    const boardW = Math.max(
      scroller.scrollWidth,
      pbEl?.scrollWidth ?? 0,
      pasteboardScrollExtents.width,
      pasteboardSize
    )
    const boardH = Math.max(
      scroller.scrollHeight,
      pbEl?.scrollHeight ?? 0,
      pasteboardScrollExtents.height,
      pasteboardSize
    )

    return computeRulerTickLayout({
      rulerUnit,
      scale,
      originX,
      originY,
      viewWidth,
      viewHeight,
      boardW,
      boardH,
      horizontalZeroAtCanvasX: snappedSpreadOriginCanvasX,
      verticalZeroAtCanvasY: verticalRulerZeroAtCanvasY,
    })
  }, [
    scale,
    pasteboardSize,
    rulerUnit,
    rulerContentOrigin,
    pasteboardScrollExtents,
    snappedSpreadOriginCanvasX,
    verticalRulerZeroAtCanvasY,
    scrollRef,
    documentCanvasRef,
    pasteboardRef,
  ])

  useLayoutEffect(() => {
    const sc = scrollRef.current

    if (!sc) return
    setRulerViewport({
      sl: sc.scrollLeft,
      st: sc.scrollTop,
      vw: sc.clientWidth,
      vh: sc.clientHeight,
    })
  }, [rulerTicks, scrollRef, setRulerViewport])

  const rulerTickInsetH = V_RULER_THICKNESS_PX + 0.5
  const rulerTickInsetV = H_RULER_THICKNESS_PX + 0.5

  const visibleRulerTicksH = useMemo(
    () =>
      viewportFilterAndCapTicks(rulerTicks.h, {
        scrollMain: rulerViewport.sl,
        viewportPx: rulerViewport.vw,
        rulerInset: rulerTickInsetH,
        overscanPx: RULER_OVERSCAN_PX,
        maxDom: RULER_MAX_DOM_TICKS_PER_AXIS,
      }),
    [rulerTicks.h, rulerViewport.sl, rulerViewport.vw, rulerTickInsetH]
  )

  const visibleRulerTicksV = useMemo(
    () =>
      viewportFilterAndCapTicks(rulerTicks.v, {
        scrollMain: rulerViewport.st,
        viewportPx: rulerViewport.vh,
        rulerInset: rulerTickInsetV,
        overscanPx: RULER_OVERSCAN_PX,
        maxDom: RULER_MAX_DOM_TICKS_PER_AXIS,
      }),
    [rulerTicks.v, rulerViewport.st, rulerViewport.vh, rulerTickInsetV]
  )

  useLayoutEffect(() => {
    syncRulerScrollTransforms()
  }, [syncRulerScrollTransforms, rulerTicks])

  return { rulerTicks, visibleRulerTicksH, visibleRulerTicksV }
}
